import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Calendar, Clock, MapPin, Users, Plus, Edit, Trash2, CheckCircle, XCircle, Filter, Coffee, Handshake, Briefcase } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { Event, EventType, EventWithAssignments, EventWithRegistrations } from '@/types/index';
import EventFormDialog from './EventFormDialog';
import CustomerEventRegistrationManager from './CustomerEventRegistration';
import { format } from 'date-fns';

interface FullEventData extends EventWithAssignments {
  customer_event_registrations?: Array<{
    id: string;
    customer_name: string;
    customer_email: string;
    attendance_status: string;
  }>;
}

interface EventManagementTabProps {
  businessId: string;
}

const EventManagementTab: React.FC<EventManagementTabProps> = ({ businessId }) => {
  const [events, setEvents] = useState<FullEventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<EventType | 'all'>('all');
  const [customerRegistrationEvent, setCustomerRegistrationEvent] = useState<Event | null>(null);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          event_assignments (
            *,
            employees:employee_id (
              name,
              email
            )
          ),
          customer_event_registrations (
            id,
            customer_name,
            customer_email,
            attendance_status
          )
        `)
        .eq('business_id', businessId)
        .order('event_date', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error("Failed to load events. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [businessId]);

  const handleDeleteEvent = async () => {
    if (!eventToDelete) {
      console.error('No event to delete');
      return;
    }
    
    console.log('Attempting to delete event:', eventToDelete.id);
    
    try {
      // First delete all event assignments
      console.log('Deleting event assignments...');
      const { error: assignmentsError } = await supabase
        .from('event_assignments')
        .delete()
        .eq('event_id', eventToDelete.id);

      if (assignmentsError) {
        console.error('Error deleting event assignments:', assignmentsError);
        throw assignmentsError;
      }
      console.log('Event assignments deleted successfully');

      // Then delete the event
      console.log('Deleting event...');
      const { error: eventError } = await supabase
        .from('events')
        .delete()
        .eq('id', eventToDelete.id);

      if (eventError) {
        console.error('Error deleting event:', eventError);
        throw eventError;
      }
      console.log('Event deleted successfully');

      toast.success("Event deleted successfully!");
      
      // Close the delete dialog first
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      
      // Then refresh the events list
      console.log('Refreshing events list...');
      await fetchEvents();
      console.log('Events list refreshed');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error("Failed to delete event. Please try again.");
    }
  };

  const handleDeleteClick = (event: Event) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingEvent(null);
    // Refresh the events list to show the new/updated event
    fetchEvents();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'attended':
        return 'bg-blue-100 text-blue-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    }
  };

  const getEventTypeColor = (eventType: EventType) => {
    switch (eventType) {
      case 'customer':
        return 'bg-green-100 text-green-800';
      case 'employee_voluntary':
        return 'bg-blue-100 text-blue-800';
      case 'employee_mandatory':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getEventTypeLabel = (eventType: EventType) => {
    switch (eventType) {
      case 'customer':
        return 'Customer Event';
      case 'employee_voluntary':
        return 'Voluntary';
      case 'employee_mandatory':
        return 'Mandatory';
      default:
        return 'Unknown';
    }
  };

  const getEventTypeIcon = (eventType: EventType) => {
    switch (eventType) {
      case 'customer':
        return <Coffee className="h-4 w-4" />;
      case 'employee_voluntary':
        return <Handshake className="h-4 w-4" />;
      case 'employee_mandatory':
        return <Briefcase className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const filteredEvents = events.filter(event => {
    if (selectedEventType === 'all') return true;
    return event.event_type === selectedEventType;
  });

  const eventsByType = {
    customer: events.filter(e => e.event_type === 'customer'),
    employee_voluntary: events.filter(e => e.event_type === 'employee_voluntary'),
    employee_mandatory: events.filter(e => e.event_type === 'employee_mandatory'),
  };

  const renderEventList = (eventList: FullEventData[], title: string, description?: string) => {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {title}
          </CardTitle>
          {description && (
            <p className="text-sm text-gray-600">{description}</p>
          )}
        </CardHeader>
        <CardContent>
          {eventList.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No events in this category</h3>
              <p className="text-gray-600 mb-4">Create your first {title.toLowerCase()} to get started.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {eventList.map((event) => (
                <Card key={event.id} className="overflow-hidden">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                    <div className="flex justify-between items-start w-full">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getEventTypeIcon(event.event_type)}
                          <CardTitle className="text-lg font-medium">{event.name}</CardTitle>
                          <Badge className={getEventTypeColor(event.event_type)}>
                            {getEventTypeLabel(event.event_type)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {format(new Date(event.event_date), 'PPP')}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {event.start_time} ({event.duration_minutes} min)
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {event.location}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {event.event_type === 'customer' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCustomerRegistrationEvent(event)}
                          >
                            <Users className="h-4 w-4 mr-2" />
                            Registrations
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditEvent(event)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(event)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {event.description && (
                      <p className="text-gray-700 mb-4">{event.description}</p>
                    )}
                    
                    {/* Display different metrics based on event type */}
                    {event.event_type === 'customer' ? (
                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium">
                            {event.customer_event_registrations?.length || 0} registered
                            {event.max_external_attendees && ` / ${event.max_external_attendees} limit`}
                          </span>
                        </div>
                        {event.external_registration_enabled && (
                          <Badge variant="outline" className="text-green-600">
                            Online Registration Open
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mb-4">
                        <Users className="h-4 w-4 text-gray-500" />
                        <span className="text-sm font-medium">
                          {event.event_assignments?.length || 0} assigned
                          {event.capacity && ` / ${event.capacity} capacity`}
                        </span>
                      </div>
                    )}

                    {/* Customer registrations */}
                    {event.event_type === 'customer' && event.customer_event_registrations && event.customer_event_registrations.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Customer Registrations:</h4>
                        <div className="grid gap-2 max-h-32 overflow-y-auto">
                          {event.customer_event_registrations.slice(0, 3).map((registration) => (
                            <div key={registration.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">
                                  {registration.customer_name}
                                </span>
                                <span className="text-sm text-gray-600">
                                  ({registration.customer_email})
                                </span>
                              </div>
                              <Badge className={getStatusColor(registration.attendance_status)}>
                                {registration.attendance_status.replace('_', ' ')}
                              </Badge>
                            </div>
                          ))}
                          {event.customer_event_registrations.length > 3 && (
                            <p className="text-sm text-gray-500 text-center">
                              +{event.customer_event_registrations.length - 3} more registrations
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Employee assignments */}
                    {event.event_type !== 'customer' && event.event_assignments && event.event_assignments.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Assigned Employees:</h4>
                        <div className="grid gap-2 max-h-32 overflow-y-auto">
                          {event.event_assignments.slice(0, 3).map((assignment) => (
                            <div key={assignment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">
                                  {assignment.employees?.name || 'Unknown'}
                                </span>
                                <span className="text-sm text-gray-600">
                                  ({assignment.employees?.email || 'No email'})
                                </span>
                              </div>
                              <Badge className={getStatusColor(assignment.attendance_status)}>
                                {assignment.attendance_status.replace('_', ' ')}
                              </Badge>
                            </div>
                          ))}
                          {event.event_assignments.length > 3 && (
                            <p className="text-sm text-gray-500 text-center">
                              +{event.event_assignments.length - 3} more assignments
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold">Event Management</h2>
            <p className="text-gray-600">Manage customer events, team building, and meetings</p>
          </div>
          <Button onClick={() => setIsFormOpen(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create Event
          </Button>
        </div>

        {/* Event Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <Coffee className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Customer Events</p>
                  <p className="text-2xl font-bold">{eventsByType.customer.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Handshake className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Voluntary Events</p>
                  <p className="text-2xl font-bold">{eventsByType.employee_voluntary.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-full">
                  <Briefcase className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Mandatory Events</p>
                  <p className="text-2xl font-bold">{eventsByType.employee_mandatory.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <TabsList className="grid w-full sm:w-auto grid-cols-4">
              <TabsTrigger value="all" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                All Events
              </TabsTrigger>
              <TabsTrigger value="customer" className="flex items-center gap-2">
                <Coffee className="h-4 w-4" />
                Customer
              </TabsTrigger>
              <TabsTrigger value="employee_voluntary" className="flex items-center gap-2">
                <Handshake className="h-4 w-4" />
                Voluntary
              </TabsTrigger>
              <TabsTrigger value="employee_mandatory" className="flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Mandatory
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="all" className="mt-6">
            {renderEventList(events, "All Events")}
          </TabsContent>
          <TabsContent value="customer" className="mt-6">
            {renderEventList(eventsByType.customer, "Customer Events", "Create events for customers like happy hours, promotions, and special offers.")}
          </TabsContent>
          <TabsContent value="employee_voluntary" className="mt-6">
            {renderEventList(eventsByType.employee_voluntary, "Voluntary Employee Events", "Team building activities, social gatherings, and optional training sessions.")}
          </TabsContent>
          <TabsContent value="employee_mandatory" className="mt-6">
            {renderEventList(eventsByType.employee_mandatory, "Mandatory Employee Events", "Required meetings, training sessions, and company announcements.")}
          </TabsContent>
        </Tabs>
      </div>

      {isFormOpen && (
        <EventFormDialog
          businessId={businessId}
          event={editingEvent}
          onClose={handleFormClose}
        />
      )}

      {customerRegistrationEvent && (
        <CustomerEventRegistrationManager
          event={customerRegistrationEvent}
          onClose={() => setCustomerRegistrationEvent(null)}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{eventToDelete?.name}"? This action cannot be undone.
              All employee assignments for this event will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteDialogOpen(false);
              setEventToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEvent}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EventManagementTab;